import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "../services/server";
import RiskProfileQuestionnaireForm from "./components/RiskProfileQuestionnaireForm";
import OwnerQuestionnaireForm from "./components/OwnerQuestionnaireForm";
import DueDiligenceChecklist from "./components/DueDiligenceChecklist";
import "./css/seller-portal.css";

/**
 * Token-based portal for a delegate (colleague invited by a seller to fill
 * in one or more of the three DD forms). No login required — the URL token
 * is the credential.
 */
export default function DDDelegatePortal() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/users/dd/delegate/${token}/`, { credentials: "omit" })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Länken är ogiltig eller har gått ut.");
        }
        const data = await res.json();
        if (!alive) return;
        setCtx(data);
        if (data.allows_risk_profile) setTab("risk");
        else if (data.allows_owner_questionnaire) setTab("owner");
        else if (data.allows_ddq_checklist) setTab("ddq");
      })
      .catch((err) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#78716C" }}>Laddar…</div>;
  }
  if (error) {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: 24, textAlign: "center" }}>
        <h1 style={{ fontSize: 22, color: "#1C1917", marginBottom: 8 }}>Åtkomst nekad</h1>
        <p style={{ color: "#78716C" }}>{error}</p>
      </div>
    );
  }
  if (!ctx) return null;

  const quizzesUrl = `/users/dd/delegate/${token}/quizzes/`;
  const tabs = [
    ctx.allows_risk_profile && { id: "risk",  label: "Riskprofil" },
    ctx.allows_owner_questionnaire && { id: "owner", label: "Ägarformulär" },
    ctx.allows_ddq_checklist && { id: "ddq",   label: "DD-checklista" },
  ].filter(Boolean);

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF9" }}>
      <header style={{ background: "#fff", borderBottom: "1px solid #E7E5E4", padding: "16px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: "#1C1917", margin: 0 }}>
            Due diligence{ctx.company_name ? ` – ${ctx.company_name}` : ""}
          </h1>
          <p style={{ fontSize: 13, color: "#78716C", margin: "4px 0 0" }}>
            Inbjuden via delegering ({ctx.email}). Endast formulären nedan är öppna för dig.
          </p>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
        <section className="sp-module" style={{ background: "#fff", borderRadius: 12, padding: 24 }}>
          <div className="sp-dd-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={"sp-dd-tab" + (tab === t.id ? " sp-dd-tab--active" : "")}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="sp-dd-tab-body">
            {tab === "risk" && (
              <RiskProfileQuestionnaireForm orgnr={token} quizzesUrl={quizzesUrl} />
            )}
            {tab === "owner" && (
              <OwnerQuestionnaireForm orgnr={token} quizzesUrl={quizzesUrl} />
            )}
            {tab === "ddq" && (
              <DueDiligenceChecklist
                company={token}
                storageKey={`delegate-ddq-${token}`}
                quizzesUrl={quizzesUrl}
              />
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
